import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { environment } from 'src/environments/environment';
import axios from 'axios';
import { TokenService } from 'src/app/auth/token.service';
import { EditorService } from 'src/app/components/editor/editor.service';

type MusicPickerMode = 'compilation' | 'mix_lyric';
type MusicSource = 'links' | 'booms' | 'groups' | 'claims' | 'promos';

const DEFAULT_LYRIC_SYNC = '[{"milliseconds":1555,"line":"..."}]';
const GROUP_ID = 'GROUP';

@Component({
  selector: 'app-music-picker',
  templateUrl: './music-picker.component.html',
  styleUrls: ['./music-picker.component.css']
})
export class MusicPickerComponent implements OnChanges, OnDestroy {
  @Input() mode: MusicPickerMode = 'compilation';
  @Input() target: any = null;
  @Input() boomGenres: any = [];
  @Input() isPortrait: boolean = false;
  @Input() validationErrors: Set<number> = new Set();
  @Input() isAutoInit: boolean = false;
  // Super collection only: backend reads these extra track keys from the saved music value.
  @Input() hasTrackDetails: boolean = false;
  @Input() hasLyricSync: boolean = false;
  @Input() hasClaimGroup: boolean = false;
  readonly sourceTabs: { key: MusicSource; label: string }[] = [
    { key: 'links', label: 'Links' },
    { key: 'booms', label: 'Boom DB' },
    { key: 'groups', label: 'Groups' },
    { key: 'claims', label: 'Claim' },
    { key: 'promos', label: 'Promo' }
  ];
  activeSource: MusicSource = 'links';
  isTracksExpanded: boolean = false;
  editingTrack: any = null;
  replaceList: unknown[] = [];
  @ViewChild('replaceSelect') replaceSelect: MatSelect;
  @ViewChild('replaceInput') replaceInput: ElementRef<HTMLInputElement>;
  username: any = null;
  isLookupLoaded: boolean = false;
  boomGroups: any = [];
  boomMusic: any = { booms: [], groups: [] };
  musicList: any = { claims: [], promos: [] };
  claimTopics: any = [];
  promoTopics: any = [];
  fukArtists: any = new Set();
  currentPlayingIndex: any = null;
  private audio: HTMLAudioElement | null = null;

  constructor(private snackBar: MatSnackBar, private tokenService: TokenService, public editorService: EditorService) {
    this.username = this.tokenService.getUsername();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isPortrait && this.isLookupLoaded) this.getBoomGroups();
    if (changes.target && this.isAutoInit && this.target) this.init();
  }

  ngOnDestroy() {
    this.stopAudio();
  }

  get dataKey(): 'music_data' | 'mix_data' {
    return this.mode == 'mix_lyric' ? 'mix_data' : 'music_data';
  }

  get compData() {
    return this.target?.comp_data;
  }

  get tracks() {
    return this.target?.[this.dataKey];
  }

  isEmptyTopic(topic: string | null | undefined) {
    return !topic || topic == 'NO_CLAIM' || topic == 'NO_PROMO';
  }

  private defaultRow(type: string) {
    return this.editorService.defaultMusicSourceRow(type);
  }

  sourceError(source: MusicSource): string | null {
    if (source != 'claims' && source != 'promos') return null;
    return this.editorService.musicSourceError(this.compData, source, this.hasClaimGroup);
  }

  validate(): string | null {
    if (!this.compData) return null;
    const sources: MusicSource[] = ['claims', 'promos'];
    for (const source of sources) {
      const error = this.sourceError(source);
      if (error) {
        this.activeSource = source;
        return error;
      }
    }
    return null;
  }

  sourceCount(source: MusicSource) {
    if (source == 'links') return (this.tracks || []).filter(x => x.music_idx && !this.editorService.parseMusicIdx(x.music_idx)).length;
    return (this.compData?.[source] || []).reduce((total, row) => total + (row.musics?.length || 0) + (row.limit || 0), 0);
  }

  init() {
    if (!this.target) return;
    this.stopAudio();
    this.stopEditing();
    this.loadLookups();
    if (!Array.isArray(this.target[this.dataKey])) this.target[this.dataKey] = [];
    if (!this.target.comp_data) this.target.comp_data = {};
    const compData = this.target.comp_data;
    if (compData.link_text === undefined) compData.link_text = '';
    for (const type of ['booms', 'groups', 'claims', 'promos']) {
      if (!compData[type]?.length) compData[type] = [this.defaultRow(type)];
    }
    this.boomMusic = { booms: [], groups: [] };
    this.musicList = { claims: [], promos: [] };
    for (const type of ['booms', 'groups']) {
      compData[type].forEach((value, i) => this.getBoomMusics(value.genre, type, i));
    }
    for (const type of ['claims', 'promos']) {
      compData[type].forEach((value, i) => this.getMusics(value.topic, type, i));
    }
  }

  reset() {
    this.stopAudio();
    this.stopEditing();
    this.target.comp_data.link_text = '';
    this.target[this.dataKey] = [];
    for (const type of ['booms', 'groups', 'claims', 'promos']) {
      this.target.comp_data[type] = [this.defaultRow(type)];
    }
    this.boomMusic = { booms: [], groups: [] };
    this.musicList = { claims: [], promos: [] };
  }

  private loadLookups() {
    if (this.isLookupLoaded) return;
    this.isLookupLoaded = true;
    this.getBoomGroups();
    this.editorService.cachedLookup('fukArtists', () => this.editorService.getFukArtists())
      .then((results: any) => this.fukArtists = new Set(results.map((e) => e.name?.toLowerCase()))).catch(() => { });
    this.editorService.cachedLookup(`claimTopics:${this.username}`, () => this.editorService.getClaims('CLAIM', 'mix', this.username))
      .then((results) => this.claimTopics = results).catch(() => { });
    this.editorService.cachedLookup(`promoTopics:${this.username}`, () => this.editorService.getClaims('PROMOS', 'mix', this.username))
      .then((results) => this.promoTopics = results).catch(() => { });
  }

  getBoomGroups() {
    this.editorService.cachedLookup(`boomGroups:${this.username}:${this.isPortrait}`, () => this.editorService.getBoomGroups(this.username, this.isPortrait))
      .then((results) => this.boomGroups = results).catch(() => { });
  }

  // Keep a reference so a late response can't land in another entry's list after init() swaps it.
  getBoomMusics(value: any, type: any, index: any) {
    if (!value) return;
    const store = this.boomMusic;
    this.editorService.getGenreSongs(value).then((results) => {
      store[type][index] = results;
      this.refreshReplaceList();
    }).catch(() => { });
  }

  getMusics(value: string, type: string, index: number): Promise<unknown[] | null> {
    if (this.isEmptyTopic(value)) return Promise.resolve(null);
    const store = this.musicList;
    return this.editorService.getClaimMusics(value, this.username)
      .then((results: any) => {
        store[type][index] = results;
        this.refreshReplaceList();
        if (!results.length) this.openSnackBar('You have uploaded more than limit videos claim per week');
        return results;
      })
      .catch(() => null);
  }

  private tracksOf(target: any) {
    return target[this.dataKey];
  }

  private removeTracks(target: any, predicate: (track: any) => boolean) {
    target[this.dataKey] = this.tracksOf(target).filter(x => !predicate(x));
  }

  private toggleTrack(target: any, obj: any, isSame: (track: any) => boolean) {
    if (this.tracksOf(target).some(isSame)) this.removeTracks(target, isSame);
    else this.addTrack(target, obj);
  }

  private addTrack(target: any, obj: any) {
    this.tracksOf(target).push(obj);
  }

  private unlinkTrackSource(target: any, item: any) {
    if (!item.music_idx || this.tracksOf(target).some(x => x.music_idx == item.music_idx)) return;
    const compData = target.comp_data;
    const source = this.editorService.parseMusicIdx(item.music_idx);
    if (source?.id == GROUP_ID) {
      delete compData[source.type][source.row].limit;
    } else if (source) {
      compData[source.type][source.row].musics = compData[source.type][source.row].musics.filter(x => x.id != source.id);
    } else {
      // Whole-line match: a substring replace would also cut a new link that starts with the old one.
      compData.link_text = compData.link_text.split(/\r?\n/).filter(x => x.trim() != item.music_idx).join('\n');
    }
  }

  editTrack(item: any) {
    if (this.editingTrack === item) {
      this.stopEditing();
      return;
    }
    this.replaceList = this.replaceChoices(item);
    this.editingTrack = item;
    setTimeout(() => {
      this.replaceSelect?.open();
      this.replaceInput?.nativeElement.focus();
    });
  }

  stopEditing() {
    this.editingTrack = null;
    this.replaceList = [];
  }

  // The replace list is a snapshot, so rebuild it when a source list finishes loading mid-edit.
  private refreshReplaceList() {
    if (this.editingTrack) this.replaceList = this.replaceChoices(this.editingTrack);
  }

  // Current song first, then songs already added, so the panel opens on what matters.
  replaceChoices(item: { music_idx?: string }): unknown[] {
    const current = this.currentSourceItem(item);
    const options: unknown[] = this.replaceOptions(item);
    const rank = (music: unknown) => music === current ? 0 : this.isPicked(item, music) ? 1 : 2;
    return options.map((music, index) => ({ music, index }))
      .sort((a, b) => rank(a.music) - rank(b.music) || a.index - b.index)
      .map(x => x.music);
  }

  scrollPanelTop(select: MatSelect) {
    if (select.panel) select.panel.nativeElement.scrollTop = 0;
  }

  replaceOptions(item: any) {
    const source = this.editorService.parseMusicIdx(item.music_idx);
    if (!source) return [];
    const options = source.type == 'booms' || source.type == 'groups' ? this.boomMusic : this.musicList;
    return options[source.type]?.[source.row] || [];
  }

  currentSourceItem(item: any) {
    const id = this.editorService.parseMusicIdx(item.music_idx)?.id;
    return this.replaceOptions(item).find(x => this.editorService.matCompareWithId(x, { id })) || null;
  }

  musicLabel(music: any) {
    if (!music) return '';
    return [music.song_name || music.title, music.artist].filter(x => x).join(' - ') || `#${music.id}`;
  }

  isPicked(item: any, music: any) {
    const source = this.editorService.parseMusicIdx(item.music_idx);
    return (this.tracks || []).some(x => x.music_idx == `${source?.type};;${source?.row};;${music.id}`);
  }

  // Swap in place without toggleTrack: its duplicate check would delete the track when the new song is already listed.
  private swapTrack(target: any, oldTrack: any, obj: any) {
    const data = this.tracksOf(target);
    const index = data.indexOf(oldTrack);
    if (index == -1) return false;
    data.splice(index, 1, obj);
    return true;
  }

  async replaceWith(track: any, music: any) {
    this.stopEditing();
    const source = this.editorService.parseMusicIdx(track.music_idx);
    if (!source || !music || this.editorService.matCompareWithId(music, { id: source.id })) return;
    const target = this.target;
    const isBoomRow = source.type == 'booms' || source.type == 'groups';
    try {
      const { obj, sourceData } = isBoomRow
        ? await this.buildBoomTrack(music, source.type, source.row, true)
        : { obj: await this.buildMusicTrack(music, source.type, source.row, true), sourceData: null };
      if (this.hasTrackDetails) await this.addTrackDetails(obj, music, sourceData);
      if (!this.swapTrack(target, track, obj)) return;
      this.unlinkTrackSource(target, track);
      const row = target.comp_data[source.type][source.row];
      if (!row.musics.some(x => x.id == music.id)) row.musics = [...row.musics, music];
    } catch (error) {
      console.log(error);
      this.openSnackBar(`Can't load track ${music.song_name || music.title || music.id}`);
    }
  }

  async replaceLink(track: any, value: string) {
    this.stopEditing();
    const link = value.trim();
    if (!link.includes('http') || link == track.music_idx) return;
    const target = this.target;
    try {
      const obj = this.mode == 'mix_lyric' ? await this.parseMixLyricLink(link) : await this.parseCompilationLink(link);
      if (this.tracksOf(target).some(x => x.uri == obj.uri)) {
        this.openSnackBar('This link is already in the list');
        return;
      }
      if (this.hasTrackDetails) await this.addTrackDetails(obj, this.linkSourceItem(obj));
      if (!this.swapTrack(target, track, obj)) return;
      const lines = target.comp_data.link_text.split(/\r?\n/);
      const lineIndex = lines.findIndex(x => x.trim() == track.music_idx);
      // A clone still uses the old link, so its line stays.
      const isShared = this.tracksOf(target).some(x => x.music_idx == track.music_idx);
      if (lineIndex != -1 && !isShared) lines[lineIndex] = link;
      else lines.push(link);
      target.comp_data.link_text = lines.join('\n');
    } catch (error) {
      console.log(error);
      this.openSnackBar(`Can't load link ${link}`);
    }
  }

  private diffById(previousValues: any[], currentValues: any[]) {
    return [
      ...previousValues.filter(x => !currentValues.some(y => y.id === x.id)),
      ...currentValues.filter(x => !previousValues.some(y => y.id === x.id))
    ];
  }

  private removeRowTracks(type: string, index: number) {
    const musics = this.compData[type][index].musics || [];
    const isBoomRow = type == 'booms' || type == 'groups';
    const groupIdx = this.groupIdx(type, index);
    this.removeTracks(this.target, x => x.music_idx == groupIdx || musics.some(y => isBoomRow
      ? x.uri.includes(y.direct_link) || x.uri.includes(y.local_id) || x.uri.includes(y.deezer_id)
      : x.uri.includes(y.local_link)));
  }

  private groupIdx(type: string, index: number) {
    return `${type};;${index};;${GROUP_ID}`;
  }

  private groupUri(topic: string) {
    return `deezer:boom:CLAIM__${topic}__${this.username}`;
  }

  isGroupTrack(item: { music_idx?: string } | null) {
    return this.editorService.parseMusicIdx(item?.music_idx)?.id == GROUP_ID;
  }

  groupTopic(item: { music_idx?: string }) {
    const source = this.editorService.parseMusicIdx(item.music_idx);
    return this.compData?.[source?.type]?.[source?.row]?.topic || '';
  }

  limitChange(type: string, index: number, value: string | number) {
    const row = this.compData[type][index];
    const max = this.musicList[type]?.[index]?.length ?? Infinity;
    const limit = Math.min(max, Math.max(0, Math.floor(Number(value)) || 0));
    const groupIdx = this.groupIdx(type, index);
    if (!limit || this.isEmptyTopic(row.topic)) {
      delete row.limit;
      this.removeTracks(this.target, x => x.music_idx == groupIdx);
      return;
    }
    row.limit = limit;
    const duration_ms = limit * 180000;
    const groupTracks = this.tracks.filter(x => x.music_idx == groupIdx);
    if (groupTracks.length) groupTracks.forEach(x => Object.assign(x, { limit, duration_ms }));
    else this.addTrack(this.target, { uri: this.groupUri(row.topic), limit, duration_ms, repeat: 1, music_type: 'CLAIM', music_idx: groupIdx, is_claim: true });
  }

  boomGenreChange(value: any, type: any, index: any) {
    this.stopAudio();
    this.compData[type][index].genre = value;
    if (!value || value == 'undefined') {
      this.boomMusic[type][index] = [];
      this.removeRowTracks(type, index);
      this.compData[type][index].musics = [];
    } else this.getBoomMusics(value, type, index);
  }

  async boomMusicChange(currentValues: any, type: any, index: any) {
    this.stopAudio();
    const target = this.target;
    const diffValues = this.diffById(target.comp_data[type][index].musics || [], currentValues);
    target.comp_data[type][index].musics = currentValues;
    for (let item of diffValues) {
      try {
        const sourceId = item.deezer_id || item.local_id;
        const { obj, sourceData } = await this.buildBoomTrack(item, type, index, !this.tracksOf(target).some(x => x.boom_deezer_id == sourceId));
        const isSame = x => x.boom_deezer_id == obj.boom_deezer_id || x.uri == obj.uri;
        if (this.hasTrackDetails && !this.tracksOf(target).some(isSame)) await this.addTrackDetails(obj, item, sourceData);
        this.toggleTrack(target, obj, isSame);
      } catch (error) {
        console.log(error);
        this.openSnackBar(`Can't load track ${item.song_name || item.id}`);
      }
    }
  }

  private async buildBoomTrack(item: any, type: any, index: any, shouldFetch: boolean) {
    const isMixLyric = this.mode == 'mix_lyric';
    let obj: any = { repeat: 1, music_idx: `${type};;${index};;${item.id}`, music_type: item.type, direct_link: item.direct_link };
    let sourceData = null;
    if (item.deezer_id) {
      Object.assign(obj, { type: 7, uri: `deezer:track:${item.deezer_id}`, boom_deezer_id: item.deezer_id });
      if (shouldFetch) {
        let uri = encodeURIComponent(`${environment.source}/deezer/track/get/${item.deezer_id}`);
        let response = await axios.get(`/pass/${uri}`);
        sourceData = response.data;
        if (isMixLyric) Object.assign(obj, this.mapLyricTrack(response.data, response.data.url_128));
        obj.duration_ms = response.data.duration * 1000 || 180000;
        if (!item.direct_link) obj.direct_link = response.data.url_128;
      }
    } else if (item.local_id) {
      Object.assign(obj, { type: 10, uri: `soundhex:track:${item.local_id}`, boom_deezer_id: item.local_id, duration_ms: item.duration_ms || 180000 });
      if (shouldFetch) {
        let uri = encodeURIComponent(`${environment.timestamp}/timestamp/${item.local_id}/`);
        let response = await axios.get(`/pass/${uri}`);
        sourceData = response.data;
        if (isMixLyric) Object.assign(obj, this.mapLyricTrack(response.data, response.data.url_128));
        else obj.uri = `direct:track:${response.data.url_128}`;
        if (!item.direct_link) obj.direct_link = response.data.url_128;
        const ms = this.lastLyricMs(response.data.lyric_sync);
        if (ms) obj.duration_ms = ms;
      }
    } else if (!isMixLyric && item.direct_link) {
      Object.assign(obj, { type: 10, uri: `direct:track:${item.direct_link}`, boom_deezer_id: item.source_id, duration_ms: item.duration_ms });
    }
    if (!isMixLyric) Object.assign(obj, { song_name: item.song_name, artist_name: item.artist });
    return { obj, sourceData };
  }

  newRow(type: string) {
    this.compData[type].push(this.defaultRow(type));
  }

  removeRow(type: string, index: number) {
    if (this.compData[type].length < 2) return;
    const options = type == 'booms' || type == 'groups' ? this.boomMusic : this.musicList;
    options[type].splice(index, 1);
    this.removeRowTracks(type, index);
    this.compData[type].splice(index, 1);
    this.shiftRowIndexes(type, index);
  }

  // music_idx stores the row position, so rows after the removed one move up by one.
  private shiftRowIndexes(type: string, removedRow: number) {
    for (const track of this.tracks) {
      const source = this.editorService.parseMusicIdx(track.music_idx);
      if (source?.type == type && Number(source.row) > removedRow) {
        track.music_idx = `${type};;${Number(source.row) - 1};;${source.id}`;
      }
    }
  }

  topicChange(event: any, type: any, index: any) {
    const value = event.target.value;
    this.compData[type][index].topic = value;
    if (this.isEmptyTopic(value)) {
      this.musicList[type][index] = [];
      this.removeRowTracks(type, index);
      this.compData[type][index].musics = [];
      delete this.compData[type][index].limit;
    } else {
      const groupIdx = this.groupIdx(type, index);
      this.tracks.filter(x => x.music_idx == groupIdx).forEach(x => x.uri = this.groupUri(value));
      const row = this.compData[type][index];
      this.getMusics(value, type, index).then((results) => {
        if (results && row === this.compData[type][index] && row.topic === value && row.limit) this.limitChange(type, index, row.limit);
      });
    }
  }

  async musicChange(currentValues: any, type: any, index: any) {
    const target = this.target;
    const diffValues = this.diffById(target.comp_data[type][index].musics || [], currentValues);
    target.comp_data[type][index].musics = currentValues;
    for (let item of diffValues) {
      const obj = await this.buildMusicTrack(item, type, index, !this.tracksOf(target).some(x => x.uri.includes(item.local_link)));
      const isSame = x => x.uri == obj.uri;
      if (this.hasTrackDetails && !this.tracksOf(target).some(isSame)) await this.addTrackDetails(obj, item);
      this.toggleTrack(target, obj, isSame);
    }
  }

  private async buildMusicTrack(item: any, type: any, index: any, shouldFetch: boolean) {
    let obj: any = { type: 9, uri: `direct:track:${item.local_link}`, repeat: 1, music_idx: `${type};;${index};;${item.id}`, music_type: item.type };
    if (this.mode == 'mix_lyric' && shouldFetch) {
      let uri = encodeURIComponent(`${environment.timestamp}/timestamp/${item.lyric_timestamp_id}/`);
      await axios.get(`/pass/${uri}`)
        .then((response) => {
          Object.assign(obj, this.mapLyricTrack(response.data, item.local_link), { duration_ms: this.lastLyricMs(response.data.lyric_sync) || 180000 });
          if (item.ignore_title) Object.assign(obj, { song_name: '.', artist_name: '...' });
        })
        .catch((error) => console.log(error));
    } else {
      Object.assign(obj, { song_name: item.title, artist_name: item.artist });
    }
    if (type == 'claims') obj.is_claim = true;
    return obj;
  }

  async linkTextChange(event: any) {
    const target = this.target;
    const previousLinks = target.comp_data.link_text ? target.comp_data.link_text.split(/\r?\n/) : [];
    target.comp_data.link_text = event.target.value;
    const currentLinks = event.target.value.split(/\r?\n/);
    const isValid = (e, i, self) => e.includes('http') && i == self.findIndex(x => x == e);
    const changes = [
      ...currentLinks.filter(x => !previousLinks.includes(x)).filter(isValid).map(link => ({ link, isAdded: true })),
      ...previousLinks.filter(x => !currentLinks.includes(x)).filter(isValid).map(link => ({ link, isAdded: false }))
    ];
    for (const change of changes) {
      try {
        const link = change.link.trim();
        const obj = this.mode == 'mix_lyric' ? await this.parseMixLyricLink(link) : await this.parseCompilationLink(link);
        const data = this.tracksOf(target);
        const musicIndex = data.findIndex(x => x.uri == obj.uri);
        if (change.isAdded && musicIndex == -1) {
          if (this.hasTrackDetails) await this.addTrackDetails(obj, this.linkSourceItem(obj));
          this.addTrack(target, obj);
        } else if (!change.isAdded && musicIndex != -1) data.splice(musicIndex, 1);
      } catch (err) {
        console.log(err);
      }
    }
  }

  private async parseCompilationLink(link: string) {
    const obj: any = { music_idx: link, repeat: 1 };
    if (link.includes('youtube.com') || link.includes('youtu.be')) {
      const videoId = link.includes('youtube.com') ? link.match(/v=(.*?)(&|$)/)[1] : link.split('/').pop();
      return Object.assign(obj, { type: 3, uri: `youtube:video:${videoId}` });
    }
    if (link.includes('deezer.com')) {
      const deezerId = link.split('/').pop();
      const res: any = await axios.get(`/track/${deezerId}`);
      return Object.assign(obj, { type: 7, uri: `deezer:track:${deezerId}`, song_name: res.data.title, artist_name: res.data.artist.name, duration_ms: res.data.duration * 1000 || 180000 });
    }
    if (link.includes('spotify.com')) {
      const spotifyId = link.match(/track\/(.*?)(\?|$)/)[1];
      const uri = encodeURIComponent(`${environment.source}/spotify/track-onl/get/${spotifyId}`);
      const res: any = await axios.get(`/pass/${uri}`);
      return Object.assign(obj, { type: 6, uri: `spotify:track:${spotifyId}`, song_name: res.data.name, artist_name: res.data.artists.map(e => e.name).join(', '), duration_ms: res.data.duration_ms || 180000 });
    }
    return Object.assign(obj, { type: 8, uri: `direct:track:${link}` });
  }

  private async parseMixLyricLink(link: string) {
    const obj: any = { repeat: 1, music_idx: link };
    if (link.includes('deezer.com')) {
      const deezerId = link.split('/').pop();
      const uri = encodeURIComponent(`${environment.source}/deezer/track/get/${deezerId}`);
      await axios.get(`/pass/${uri}`)
        .then((response) => Object.assign(obj, this.mapLyricTrack(response.data, response.data.url_128), { duration_ms: response.data.duration * 1000 || 180000 }))
        .catch((error) => console.log(error));
      return Object.assign(obj, { type: 7, uri: `deezer:track:${deezerId}` });
    }
    if (link.includes('soundhex.com')) {
      const tsId = link.match(/timestamp\/(.*?)(\/|$)/)[1];
      const uri = encodeURIComponent(`${environment.timestamp}/timestamp/${tsId}/`);
      await axios.get(`/pass/${uri}`)
        .then((response) => Object.assign(obj, this.mapLyricTrack(response.data, response.data.url_128), { duration_ms: this.lastLyricMs(response.data.lyric_sync) || 180000 }))
        .catch((error) => console.log(error));
      return Object.assign(obj, { type: 10, uri: `soundhex:track:${tsId}` });
    }
    return Object.assign(obj, { type: 8, uri: `direct:track:${link}` });
  }

  private async addTrackDetails(obj: any, item: any, sourceData?: any) {
    Object.assign(obj, { name: obj.song_name, url_128: obj.direct_link || item?.local_link });
    if (item?.deezer_id) Object.assign(obj, { id: item.deezer_id, deezer_id: item.deezer_id });
    else if (item?.local_id) Object.assign(obj, { id: item.local_id, local_id: item.local_id });
    if (!this.hasLyricSync) return;
    const data = sourceData || await this.getTrackSource(item);
    if (!data) return;
    obj.lyric_sync = data.lyric_sync || DEFAULT_LYRIC_SYNC;
    if (!obj.url_128) obj.url_128 = data.url_128;
  }

  // Same lyric sources as mix_lyric; tracks without a deezer/local/timestamp id have none.
  private async getTrackSource(item: any) {
    const path = item?.deezer_id ? `${environment.source}/deezer/track/get/${item.deezer_id}`
      : item?.local_id ? `${environment.timestamp}/timestamp/${item.local_id}/`
      : item?.lyric_timestamp_id ? `${environment.timestamp}/timestamp/${item.lyric_timestamp_id}/`
      : null;
    if (!path) return null;
    try {
      const response = await axios.get(`/pass/${encodeURIComponent(path)}`);
      return response.data || {};
    } catch (error) {
      console.log(error);
      return {};
    }
  }

  // Deezer / Soundhex links carry their id in the uri.
  private linkSourceItem(obj: any) {
    const id = String(obj.uri || '').split(':')[2];
    const value = /^\d+$/.test(id) ? Number(id) : id;
    if (obj.uri?.startsWith('deezer:track:')) return { id: value, deezer_id: value };
    if (obj.uri?.startsWith('soundhex:track:')) return { id: value, local_id: value };
    return null;
  }

  private mapLyricTrack(data: any, audioUrl: string) {
    return {
      song_name: data.title,
      artist_name: data.artist,
      lyric_sync: data.lyric_sync || DEFAULT_LYRIC_SYNC,
      audio_url: audioUrl,
      audio_ext: this.getExt(audioUrl)
    };
  }

  private lastLyricMs(lyricSync: any) {
    try {
      return JSON.parse(lyricSync).pop().milliseconds;
    } catch (error) {
      return null;
    }
  }

  private getExt(filename: any) {
    const parts = filename ? filename.split('.') : '';
    return parts[parts.length - 1];
  }

  trackDrop(event: any) {
    moveItemInArray(this.tracks, event.previousIndex, event.currentIndex);
  }

  deleteMusic(item: any, index: number) {
    this.stopAudio();
    this.tracks.splice(index, 1);
    this.unlinkTrackSource(this.target, item);
    if (this.editingTrack === item) this.stopEditing();
  }

  cloneMusic(item: any) {
    this.stopAudio();
    // A copy, so editing or replacing one row never affects its clone.
    this.tracks.push({ ...item });
  }

  isClaimOrPromo(item: any) {
    const type = this.editorService.getMusicSourceType(item);
    return type == 'CLAIM' || type == 'PROMO';
  }

  hasValidationError(index: number): boolean {
    return this.validationErrors?.has(index);
  }

  playAudio(item: any, index: number) {
    if (!item.direct_link) return;
    if (this.isPlaying(item, index)) {
      this.stopAudio();
      return;
    }
    this.stopAudio();
    this.audio = new Audio(item.direct_link);
    this.currentPlayingIndex = `${index}_${item.id}`;
    this.audio.play().catch(err => {
      console.error('Error playing audio:', err);
      this.currentPlayingIndex = null;
    });
    this.audio.onended = () => this.currentPlayingIndex = null;
  }

  isPlaying(item: any, index: number): boolean {
    return this.currentPlayingIndex === `${index}_${item.id}` && this.audio !== null && !this.audio.paused;
  }

  stopAudio() {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    this.currentPlayingIndex = null;
  }

  openSnackBar(message: string, duration: number = 5000) {
    this.snackBar.open(message, '', { duration, horizontalPosition: 'right', verticalPosition: 'top' });
  }
}
